<template>
  <div class="popover-panel" :class="{ 'is-sheet': sheet }">
    <div v-if="sheet" class="sheet-handle" aria-hidden="true" />

    <div class="panel-header">
      <div class="header-bg" />
      <div class="header-body">
        <div class="avatar-wrap" :class="{ 'is-member': isMember }">
          <default-user-avatar size="md" :member="isMember" :src="user.avatar" />
          <span v-if="isMember" class="member-dot" :title="$t('user.member')">
            <i class="el-icon-medal" />
          </span>
        </div>
        <div class="header-info">
          <div class="name-row">
            <span class="display-name">{{ user.username }}</span>
            <span v-if="isMember" class="member-tag">{{ $t('user.member') }}</span>
            <span v-else class="user-tag">{{ $t('user.normal') }}</span>
          </div>
          <p class="user-email">{{ user.email }}</p>
          <p v-if="isMember && user.memberExpireAt" class="member-expire">
            <i class="el-icon-time" />
            {{ $t('user.expireUntil', { date: user.memberExpireAt }) }}
          </p>
          <p v-else class="member-hint">
            <i class="el-icon-star-off" />
            {{ $t('user.openHint') }}
          </p>
          <div
            v-if="pointsEnabled"
            class="points-chip"
            @click="$emit('navigate', { type: 'profile-tab', tab: 'points' })"
          >
            <i class="el-icon-coin" />
            <span>{{ userPoints }} {{ pointsName }}</span>
            <i class="el-icon-arrow-right chip-arrow" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="checkinEnabled" class="checkin-quick">
      <div class="checkin-quick-info">
        <div class="checkin-quick-icon" :class="{ done: checkedInToday }">
          <i :class="checkedInToday ? 'el-icon-circle-check' : 'el-icon-date'" />
        </div>
        <div class="checkin-quick-text">
          <strong>{{ checkedInToday ? $t('user.checkedInToday') : $t('user.checkinDaily') }}</strong>
          <span v-if="checkedInToday">{{ $t('user.streak', { n: checkinStreak }) }}</span>
          <span v-else>
            {{ $t('user.checkinReward', { n: todayReward, name: pointsName }) }}
            <template v-if="checkinStreak > 0"> · {{ $t('user.streak', { n: checkinStreak }) }}</template>
          </span>
        </div>
      </div>
      <button
        type="button"
        class="checkin-quick-btn"
        :class="{ done: checkedInToday }"
        :disabled="checkedInToday || checkingIn"
        @click="$emit('checkin')"
      >
        <i v-if="checkingIn" class="el-icon-loading" />
        <template v-else>{{ checkedInToday ? $t('user.checkedIn') : $t('user.checkin') }}</template>
      </button>
    </div>

    <div class="quick-nav">
      <button type="button" class="quick-item" @click="$emit('navigate', { type: 'profile' })">
        <span class="quick-icon profile"><i class="el-icon-user" /></span>
        <span class="quick-label">{{ $t('user.profile') }}</span>
      </button>
      <button
        v-if="pointsEnabled"
        type="button"
        class="quick-item"
        @click="$emit('navigate', { type: 'profile-tab', tab: 'points' })"
      >
        <span class="quick-icon points"><i class="el-icon-coin" /></span>
        <span class="quick-label">{{ $t('user.myPoints') }}</span>
        <span class="quick-badge">{{ userPoints }}</span>
      </button>
      <button type="button" class="quick-item" @click="$emit('navigate', { type: 'sponsor' })">
        <span class="quick-icon sponsor"><i class="el-icon-medal" /></span>
        <span class="quick-label">{{ isMember ? $t('profile.renew') : $t('profile.openSponsor') }}</span>
      </button>
      <button
        type="button"
        class="quick-item"
        @click="$emit('navigate', { type: 'profile-tab', tab: 'history' })"
      >
        <span class="quick-icon history"><i class="el-icon-time" /></span>
        <span class="quick-label">{{ $t('user.history') }}</span>
        <span v-if="historyCount" class="quick-badge">{{ historyCount }}</span>
      </button>
    </div>

    <div class="recent-section">
      <div class="section-head">
        <span class="section-title">
          <i class="el-icon-reading" />
          {{ $t('user.recent') }}
        </span>
        <button
          v-if="recentHistory.length"
          type="button"
          class="section-link"
          @click="$emit('navigate', { type: 'profile-tab', tab: 'history' })"
        >
          {{ $t('user.viewAll') }}
        </button>
      </div>

      <ul v-if="recentHistory.length" class="recent-list">
        <li
          v-for="item in recentHistory"
          :key="item.id + item.viewedAt"
          class="recent-item"
          @click="$emit('navigate', { type: 'article', id: item.id })"
        >
          <span class="recent-dot" />
          <div class="recent-body">
            <span class="recent-title">{{ item.title }}</span>
            <span class="recent-meta">{{ formatTime(item.viewedAt) }}</span>
          </div>
          <i class="el-icon-arrow-right recent-arrow" />
        </li>
      </ul>

      <div v-else class="recent-empty">
        <i class="el-icon-document" />
        <span>{{ $t('user.noHistory') }}</span>
      </div>
    </div>

    <div class="panel-footer">
      <button type="button" class="logout-btn" @click="$emit('logout')">
        <i class="el-icon-switch-button" />
        {{ $t('auth.logout') }}
      </button>
    </div>
  </div>
</template>

<script>
import DefaultUserAvatar from '@/components/DefaultUserAvatar.vue'

export default {
  name: 'UserMenuPanel',
  components: { DefaultUserAvatar },
  props: {
    sheet: { type: Boolean, default: false },
    user: { type: Object, required: true },
    isMember: { type: Boolean, default: false },
    pointsEnabled: { type: Boolean, default: true },
    pointsName: { type: String, default: '积分' },
    userPoints: { type: Number, default: 0 },
    checkinEnabled: { type: Boolean, default: true },
    checkedInToday: { type: Boolean, default: false },
    checkinStreak: { type: Number, default: 0 },
    todayReward: { type: Number, default: 10 },
    checkingIn: { type: Boolean, default: false },
    recentHistory: { type: Array, default: () => [] },
    historyCount: { type: Number, default: 0 }
  },
  methods: {
    formatTime(iso) {
      const d = new Date(iso)
      const now = new Date()
      const diff = now - d
      if (diff < 60000) return this.$t('user.justNow')
      if (diff < 3600000) return this.$t('user.minutesAgo', { n: Math.floor(diff / 60000) })
      if (diff < 86400000) return this.$t('user.hoursAgo', { n: Math.floor(diff / 3600000) })
      return d.toLocaleDateString(this.$i18n.locale === 'en' ? 'en-US' : 'zh-CN')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.popover-panel {
  margin: -12px;

  &.is-sheet {
    margin: 0;
    max-height: min(88vh, 720px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
}

.sheet-handle {
  width: 36px;
  height: 4px;
  margin: 10px auto 4px;
  border-radius: 999px;
  background: #cbd5e1;
}

.panel-header {
  position: relative;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
}

.is-sheet .panel-header {
  border-radius: 0;
}

.header-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%);
  opacity: 0.95;

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -10%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
}

.header-body {
  position: relative;
  display: flex;
  gap: 14px;
  padding: 20px 18px 18px;
  z-index: 1;
}

.avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.member-dot {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #fff;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(217, 119, 6, 0.4);
}

.header-info {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.display-name {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.member-tag,
.user-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.member-tag {
  background: rgba(251, 191, 36, 0.25);
  color: #fef3c7;
  border: 1px solid rgba(251, 191, 36, 0.4);
}

.user-tag {
  background: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.user-email {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-expire,
.member-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);

  i { font-size: 12px; }
}

.points-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fef3c7;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  .chip-arrow {
    font-size: 10px;
    opacity: 0.8;
  }
}

.checkin-quick {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 12px 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 55%, #eff6ff 100%);
  border: 1px solid #ddd6fe;
}

.checkin-quick-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.checkin-quick-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7c3aed, #6366f1);
  color: #fff;
  font-size: 18px;
  box-shadow: 0 4px 10px rgba(124, 58, 237, 0.25);

  &.done {
    background: linear-gradient(135deg, #10b981, #059669);
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.25);
  }
}

.checkin-quick-text {
  min-width: 0;

  strong {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: $text-primary;
    margin-bottom: 2px;
  }

  span {
    display: block;
    font-size: 11px;
    color: $text-secondary;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.checkin-quick-btn {
  flex-shrink: 0;
  min-width: 64px;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #7c3aed, #6366f1);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.35);
  }

  &:disabled {
    cursor: default;
    opacity: 1;
  }

  &.done {
    background: #ecfdf5;
    color: #059669;
    box-shadow: none;
    border: 1px solid #a7f3d0;
  }
}

.quick-nav {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 14px 16px 12px;
  background: $bg-white;
}

.quick-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 4px;
  border: 1px solid $border-color;
  border-radius: 10px;
  background: $bg-gray;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:active {
    border-color: $primary-border;
    background: $primary-bg;
  }

  @media (hover: hover) {
    &:hover {
      border-color: $primary-border;
      background: $primary-bg;
      transform: translateY(-1px);
      box-shadow: $shadow-sm;

      .quick-icon {
        transform: scale(1.05);
      }
    }
  }
}

.quick-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: transform 0.2s;

  &.profile {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: $primary-color;
  }

  &.sponsor {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #d97706;
  }

  &.history {
    background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
    color: #6366f1;
  }

  &.points {
    background: linear-gradient(135deg, #ede9fe, #ddd6fe);
    color: #7c3aed;
  }
}

.quick-label {
  font-size: 11px;
  font-weight: 500;
  color: $text-secondary;
  white-space: nowrap;
}

.quick-badge {
  position: absolute;
  top: 6px;
  right: 8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: $primary-color;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
}

.recent-section {
  padding: 0 16px 12px;
  background: $bg-white;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: $text-primary;

  i {
    color: $primary-color;
    font-size: 14px;
  }
}

.section-link {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: $primary-color;
  cursor: pointer;
}

.recent-list {
  list-style: none;
  max-height: 168px;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 0 4px;

  .is-sheet & {
    max-height: none;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: $border-color;
    border-radius: 4px;
  }
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:last-child { margin-bottom: 0; }

  &:active {
    background: $bg-gray;

    .recent-title { color: $primary-color; }
  }
}

.recent-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $primary-border;
  flex-shrink: 0;
}

.recent-body {
  flex: 1;
  min-width: 0;
}

.recent-title {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: $text-primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}

.recent-meta {
  display: block;
  font-size: 11px;
  color: $text-muted;
  margin-top: 1px;
}

.recent-arrow {
  font-size: 12px;
  color: $text-muted;
  flex-shrink: 0;
  opacity: 0.5;
}

.recent-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 0;
  color: $text-muted;
  font-size: 12px;

  i {
    font-size: 28px;
    opacity: 0.4;
  }
}

.panel-footer {
  padding: 10px 16px 14px;
  background: $bg-white;
  border-top: 1px solid $border-color;
  border-radius: 0 0 12px 12px;
}

.is-sheet .panel-footer {
  border-radius: 0;
  padding-bottom: max(14px, env(safe-area-inset-bottom, 0px));
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: $text-secondary;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    background: #fef2f2;
    color: #ef4444;
  }

  i { font-size: 14px; }
}

@media (max-width: 400px) {
  .quick-nav {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
